import { useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import usePoll from "@/hooks/usePoll";
import { useAuth } from "@/context/AuthContext";
import { api, defMethod, errMsg, uid, waLink } from "@/lib/api";
import { HandCoins, MessageCircle, Printer } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { SaleActionButtons, SaleInfoDialog } from "@/components/SaleActions";
import { DeliveryBadge, PayBadge } from "@/components/common";
import { Empty, ErrorBox, ExportBar, Field, Loading, Modal, Money, PageHeader } from "@/components/common";

const EMPTY = { full_name: "", phone: "", id_number: "", address: "", notes: "" };

function CustomerForm({ open, onOpenChange, initial, onDone }) {
  const [f, setF] = useState(initial || EMPTY);
  const [err, setErr] = useState("");
  const save = async () => {
    setErr("");
    try {
      const body = { ...f, id_number: f.id_number || null, address: f.address || null, notes: f.notes || null };
      if (initial?.id) await api.put(`/customers/${initial.id}`, body); else await api.post("/customers", body);
      toast.success("تم حفظ بيانات الزبون"); onOpenChange(false); onDone();
    } catch (e) { setErr(errMsg(e)); }
  };
  const inp = (k, p = {}) => <input className="k-input" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`customer-form-${k}`} {...p} />;
  return (
    <Modal open={open} onOpenChange={onOpenChange} title={initial?.id ? "تعديل بيانات الزبون" : "تسجيل زبون جديد"}
      footer={<button className="k-btn-primary" onClick={save} data-testid="customer-form-save">حفظ</button>}>
      <div className="grid gap-3">
        <Field label="الاسم الكامل *">{inp("full_name")}</Field>
        <Field label="رقم الهاتف *">{inp("phone", { inputMode: "tel" })}</Field>
        <Field label="رقم تعريف إضافي">{inp("id_number")}</Field>
        <Field label="العنوان أو المنطقة">{inp("address")}</Field>
        <Field label="ملاحظات">{inp("notes")}</Field>
        <ErrorBox text={err} />
      </div>
    </Modal>
  );
}

function CustomerDialog({ customer, onClose, onEdit }) {
  const { can, settings } = useAuth();
  const { data, reload } = usePoll(can("view_sales") ? "/sales" : null, { customer_id: customer.id, status: "all" }, 20000);
  const [editSale, setEditSale] = useState(null);
  const [sel, setSel] = useState([]);
  const toggle = (id) => setSel((o) => (o.includes(id) ? o.filter((x) => x !== id) : [...o, id]));
  const printSel = () => window.open(`/print/customer/${customer.id}?sales=${sel.join(",")}&pdf=1`, "_blank");
  const permitSel = async () => {
    try {
      const { data: r } = await api.post("/permits/batch", { sale_ids: sel });
      r.errors.forEach((e) => toast.error(`${e.ear_tag || e.number}: ${e.reason}`));
      if (r.permits.length) { toast.success(`${r.permits.length} تصريح جاهز`); window.open(`/print/permits?ids=${r.permits.map((p) => p.id).join(",")}&pdf=1`, "_blank"); reload(); }
    } catch (e) { toast.error(errMsg(e)); }
  };
  const openSales = (data || []).filter((s) => s.status === "active" && s.remaining > 0);
  const totalRem = openSales.reduce((a, s) => a + s.remaining, 0);
  const selRem = openSales.filter((s) => sel.includes(s.id)).reduce((a, s) => a + s.remaining, 0);
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({ amount: "", method: "", reference: "" });
  const [payKey, setPayKey] = useState(uid());
  const [payBusy, setPayBusy] = useState(false);
  const [payErr, setPayErr] = useState("");
  const amt = Number(pay.amount || 0);
  const isFull = amt === totalRem;
  const openPay = () => { setPay({ amount: String(totalRem), method: defMethod(settings), reference: "" }); setPayErr(""); setPayOpen(true); };
  const doPay = async () => {
    setPayErr("");
    if (!amt) return setPayErr("يرجى إدخال المبلغ");
    if (!isFull && !selRem) return setPayErr("المبلغ أقل من كامل المتبقي: حدّد الكباش التي تدفع لها هذه الدفعة من القائمة");
    if (!isFull && amt > selRem) return setPayErr(`المبلغ يتجاوز متبقي الكباش المحددة (${selRem.toLocaleString("en-US")} دج)`);
    setPayBusy(true);
    try {
      const { data: r } = await api.post(`/customers/${customer.id}/payments`, { amount: amt, method: pay.method, reference: pay.reference || null, idempotency_key: payKey, ...(isFull ? {} : { sale_ids: sel }) });
      toast.success(`تم تسجيل الدفعة على ${r.payments.length} كبش`);
      setPayKey(uid()); setPayOpen(false); reload();
      window.open(`/print/customer/${customer.id}?sales=${[...new Set(r.sale_ids)].join(",")}&pdf=1`, "_blank");
    } catch (e) { setPayErr(errMsg(e) + (e.response ? "" : " – يمكنك إعادة المحاولة دون تكرار.")); } finally { setPayBusy(false); }
  };
  const msg = `${settings?.org_name || "الربيع للأضاحي"}\nالسيد(ة) ${customer.full_name}، رصيدكم المتبقي: ${customer.remaining.toLocaleString("en-US")} دج`;
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} wide title={customer.full_name} description={`${customer.phone}${customer.address ? " · " + customer.address : ""}`}>
      <div className="space-y-4" data-testid="customer-dialog">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-xl bg-[#F6F1E7] p-2"><div className="text-xs text-slate-500">الإجمالي</div><Money v={customer.total} /></div>
          <div className="rounded-xl bg-emerald-50 p-2"><div className="text-xs text-slate-500">المدفوع</div><Money v={customer.paid} className="text-emerald-700" /></div>
          <div className="rounded-xl bg-rose-50 p-2"><div className="text-xs text-slate-500">المتبقي</div><Money v={customer.remaining} className="text-rose-700" /></div>
        </div>
        <div className="flex flex-wrap gap-2">
          {can("manage_customers") && <button className="k-btn-outline" onClick={onEdit} data-testid="customer-dialog-edit"><Pencil className="w-4 h-4" /> تعديل بيانات الزبون</button>}
          <a className="k-btn-outline text-emerald-700" href={waLink(customer.phone, msg)} target="_blank" rel="noreferrer" data-testid="customer-dialog-wa"><MessageCircle className="w-4 h-4" /> واتساب</a>
          {can("record_payment") && totalRem > 0 && <button className="k-btn-primary" onClick={openPay} data-testid="customer-account-pay"><HandCoins className="w-4 h-4" /> دفعة على الحساب</button>}
        </div>
        {can("view_sales") && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 justify-between">
              <div className="font-display font-bold">مشتريات الزبون</div>
              {data?.length > 0 && (
                <div className="flex gap-2">
                  <button className="k-btn-outline k-btn-sm" onClick={() => setSel(sel.length === data.length ? [] : data.map((s) => s.id))} data-testid="select-all-sales">{sel.length === data.length ? "إلغاء التحديد" : "تحديد الكل"}</button>
                  <button className="k-btn-primary k-btn-sm" disabled={!sel.length} onClick={printSel} data-testid="print-combined-receipt"><Printer className="w-4 h-4" /> طباعة وصل مجمّع ({sel.length})</button>
                  {can("issue_permit") && <button className="k-btn-accent k-btn-sm" disabled={!sel.length} onClick={permitSel} data-testid="issue-combined-permits">تصاريح خروج للمحدد ({sel.length})</button>}
                </div>
              )}
            </div>
            {(data || []).map((s) => (
              <div key={s.id} className="rounded-xl border p-3 space-y-2" data-testid={`customer-sale-${s.number}`}>
                <div className="flex flex-wrap items-center gap-2 justify-between">
                  <label className="flex items-center gap-2 cursor-pointer"><Checkbox checked={sel.includes(s.id)} onCheckedChange={() => toggle(s.id)} data-testid={`select-sale-${s.number}`} /><span><b className="num">{s.number}</b> · كبش <b className="num">{s.ear_tag}</b> · {s.section_name}{s.category && ` · ${s.category}`}{s.status === "cancelled" && <span className="text-rose-700 font-bold"> · ملغاة</span>}</span></label>
                  <div className="text-sm">المتبقي <Money v={s.remaining} className="text-rose-700" /></div>
                </div>
                <div className="flex gap-2"><PayBadge s={s.payment_status} /><DeliveryBadge s={s.delivery_status} /></div>
                <SaleActionButtons sale={s} compact onEdit={() => setEditSale(s)} />
              </div>
            ))}
            {data && !data.length && <Empty text="لا توجد مشتريات" />}
          </div>
        )}
      </div>
      <Modal open={payOpen} onOpenChange={setPayOpen} title="دفعة على حساب الزبون" description={`المتبقي الكلي: ${totalRem.toLocaleString("en-US")} دج – المحدد: ${selRem.toLocaleString("en-US")} دج`}
        footer={<button className="k-btn-primary" onClick={doPay} disabled={payBusy} data-testid="account-pay-submit">تسجيل الدفعة</button>}>
        <div className="grid gap-3">
          <Field label="المبلغ (دج)"><input className="k-input num text-right text-xl" inputMode="numeric" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value.replace(/\D/g, "") })} data-testid="account-pay-amount" /></Field>
          <Field label="طريقة الدفع"><select className="k-input" value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} data-testid="account-pay-method">{(settings?.payment_methods || ["نقدًا"]).map((m) => <option key={m}>{m}</option>)}</select></Field>
          <Field label="مرجع (اختياري)"><input className="k-input" value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} data-testid="account-pay-ref" /></Field>
          <div className="text-sm rounded-xl bg-[#F6F1E7] p-3" data-testid="account-pay-hint">{isFull ? "المبلغ يساوي كامل المتبقي: ستُسدّد كل الكباش تلقائيًا ويصبح المتبقي صفرًا." : `المبلغ جزئي: يُوزع بالترتيب على الكباش المحددة في القائمة (${sel.length} محدد).`}</div>
          <ErrorBox text={payErr} />
        </div>
      </Modal>
      {editSale && <SaleInfoDialog open onOpenChange={(o) => !o && setEditSale(null)} sale={editSale} onDone={reload} />}
    </Modal>
  );
}

export default function Customers() {
  const { can } = useAuth();
  const [q, setQ] = useState("");
  const [applied, setApplied] = useState("");
  const [form, setForm] = useState(null);
  const [picked, setPicked] = useState(null);
  const { data, error, loading, reload } = usePoll("/customers", applied ? { q: applied } : null, 30000);
  const rows = data || [];
  return (
    <div>
      <PageHeader title="الزبائن" subtitle="سجل الزبائن وأرصدتهم المتبقية">
        <ExportBar name="customers" rows={rows} cols={[{ key: "full_name", label: "الاسم" }, { key: "phone", label: "الهاتف" }, { key: "id_number", label: "رقم التعريف" },
          { key: "address", label: "العنوان" }, { key: "sales_count", label: "عدد المشتريات" }, { key: "total", label: "الإجمالي" }, { key: "paid", label: "المدفوع" }, { key: "remaining", label: "المتبقي" }]} />
        <button className="k-btn-primary" onClick={() => setForm({})} data-testid="add-customer-button"><Plus className="w-5 h-5" /> زبون جديد</button>
      </PageHeader>
      <form className="mb-4 max-w-lg" onSubmit={(e) => { e.preventDefault(); setApplied(q); }}>
        <input className="k-input" placeholder="بحث بالاسم أو الهاتف ثم Enter" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customers-search-input" />
      </form>
      <ErrorBox text={error} />
      {loading && !data ? <Loading /> : (
        <div className="k-card overflow-x-auto">
          <table className="k-table" data-testid="customers-table">
            <thead><tr><th>الاسم</th><th>الهاتف</th><th>المنطقة</th><th>عدد المشتريات</th><th>الإجمالي</th><th>المدفوع</th><th>المتبقي</th><th></th></tr></thead>
            <tbody>{rows.map((c) => (
              <tr key={c.id} className="cursor-pointer" onClick={(e) => { if (!e.target.closest("a,button")) setPicked(c); }} data-testid={`customer-row-${c.phone}`}>
                <td className="font-bold">{c.full_name}</td><td className="num">{c.phone}</td><td>{c.address || "—"}</td><td className="num">{c.sales_count}</td>
                <td><Money v={c.total} /></td><td><Money v={c.paid} className="text-emerald-700" /></td><td><Money v={c.remaining} className="text-rose-700" /></td>
                <td className="whitespace-nowrap">
                  {can("view_sales") && <Link className="k-btn-outline k-btn-sm" to={`/sales?customer=${c.id}`}>المبيعات</Link>}
                  {can("manage_customers") && <button className="k-btn-outline k-btn-sm mr-1" onClick={() => setForm(c)} data-testid={`edit-customer-${c.id}`}><Pencil className="w-4 h-4" /></button>}
                </td>
              </tr>))}</tbody>
          </table>
          {!rows.length && <Empty text="لا يوجد زبائن" />}
        </div>
      )}
      {picked && <CustomerDialog customer={picked} onClose={() => setPicked(null)} onEdit={() => { setForm(picked); setPicked(null); }} />}
      {form && <CustomerForm open={!!form} onOpenChange={(o) => !o && setForm(null)} initial={form.id ? form : null} onDone={reload} />}
    </div>
  );
}
